import { Box, Input, List, Portal, Spinner, Text } from '@chakra-ui/react';
import { useCombobox } from 'downshift';
import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useJobSearchQuery } from '../apis/useJobSearchQuery';
import type { JobSearchResult } from '../types/JobSearchResult';

type JobSearchInputProps = {
  mode: 'navigate' | 'select';
  value?: string;
  onValueChange?: (value: string, selectedItem?: JobSearchResult | null) => void;
  onNavigate?: (job: JobSearchResult) => void;
  placeholder?: string;
  width?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  clearOnBlur?: boolean;
};

function useAnchorRect(ref: RefObject<HTMLElement | null>, track: boolean) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!track || !ref.current) {
      setRect(null);
      return;
    }

    const updateRect = () => {
      setRect(ref.current?.getBoundingClientRect() ?? null);
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [ref, track]);

  return rect;
}

function isStatus(status: string | null | undefined, expected: string) {
  return status?.trim().toLowerCase() === expected.toLowerCase();
}

function getJobSearchResultStyle(item: JobSearchResult, isHighlighted: boolean) {
  if (isStatus(item.StatusName, 'Cancelled') || isStatus(item.InvoiceStatusName, 'Cancelled')) {
    return {
      bg: isHighlighted ? 'red.200' : 'red.50',
      color: 'red.700'
    };
  }

  if (item.IsEnvironmental) {
    return {
      bg: isHighlighted ? 'green.200' : 'green.100',
      color: 'green.800'
    };
  }

  if (isStatus(item.StatusName, 'Complete')) {
    return {
      bg: isHighlighted ? 'blue.200' : 'blue.100',
      color: 'blue.800'
    };
  }

  return {
    bg: isHighlighted ? 'gray.100' : 'white',
    color: 'black'
  };
}

function getJobSearchResultLabel(item: JobSearchResult) {
  if (item.JobIdDisplayName && item.JobIdDisplayName !== item.JobIdString) {
    return item.JobIdDisplayName;
  }

  return `${item.JobIdString} - ${item.ClientName}`;
}

export function JobSearchInput({
  mode,
  value,
  onValueChange,
  onNavigate,
  placeholder,
  width = '280px',
  size = 'sm',
  clearOnBlur = mode === 'navigate'
}: JobSearchInputProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [internalValue, setInternalValue] = useState('');
  const inputValue = value ?? internalValue;
  const [searchTerm, setSearchTerm] = useState(inputValue);
  const jobSearchQuery = useJobSearchQuery(searchTerm);
  const items: JobSearchResult[] = jobSearchQuery.data ?? [];

  useEffect(() => {
    if (value !== undefined && value.length < 2) {
      setSearchTerm(value);
    }
  }, [value]);

  const debounceSearch = useDebouncedCallback((nextValue: string) => {
    setSearchTerm(nextValue);
  }, 300);

  const setValue = (nextValue: string, selectedItem?: JobSearchResult | null) => {
    if (value === undefined) {
      setInternalValue(nextValue);
    }

    onValueChange?.(nextValue, selectedItem);
  };

  const { isOpen, getMenuProps, getInputProps, getItemProps, highlightedIndex, setInputValue, reset } =
    useCombobox<JobSearchResult>({
      items,
      inputValue,
      itemToString: (item) => (item ? getJobSearchResultLabel(item) : ''),
      onInputValueChange: (changes) => {
        const nextValue = changes.inputValue;
        const nextInputValue = nextValue ?? '';
        debounceSearch(nextInputValue);

        if (changes.type === useCombobox.stateChangeTypes.InputChange) {
          setValue(nextInputValue, null);
        }
      },
      onSelectedItemChange: ({ selectedItem }) => {
        if (!selectedItem) {
          return;
        }

        if (mode === 'navigate') {
          onNavigate?.(selectedItem);
          setInternalValue('');
          setSearchTerm('');
          setInputValue('');
          reset();
          return;
        }

        setValue(selectedItem.JobIdString, selectedItem);
        setSearchTerm(selectedItem.JobIdString);
        setInputValue(selectedItem.JobIdString);
      }
    });

  const showMenu =
    isOpen && searchTerm.length >= 2 && (items.length > 0 || jobSearchQuery.isFetching || jobSearchQuery.data != null);
  const anchorRect = useAnchorRect(containerRef, showMenu);

  return (
    <Box ref={containerRef} position='relative' width={width}>
      <Input
        {...getInputProps({
          placeholder: placeholder ?? (mode === 'navigate' ? 'Go to Job...' : 'Job ID'),
          onBlur: () => {
            if (!clearOnBlur) {
              return;
            }

            setValue('', null);
            setSearchTerm('');
            setInputValue('');
          }
        })}
        size={size}
        variant='outline'
      />
      <Portal>
        <List.Root
          {...getMenuProps()}
          position='fixed'
          top={anchorRect ? `${anchorRect.bottom + 4}px` : '0'}
          left={anchorRect ? `${anchorRect.left}px` : '0'}
          width={anchorRect ? `${anchorRect.width * 1.3}px` : width}
          zIndex={9999}
          bg='white'
          borderRadius='md'
          boxShadow='lg'
          maxHeight='300px'
          overflowY='auto'>
          {showMenu && (
            <>
              {jobSearchQuery.isFetching && items.length === 0 && (
                <List.Item px={4} py={2} display='flex' justifyContent='center'>
                  <Spinner size='sm' />
                </List.Item>
              )}
              {items.map((item, index) => {
                const style = getJobSearchResultStyle(item, highlightedIndex === index);

                return (
                  <List.Item
                    key={`${item.JobIdString}`}
                    px={4}
                    py={2}
                    cursor='pointer'
                    bg={style.bg}
                    _hover={{ bg: style.bg }}
                    {...getItemProps({ item, index })}>
                    <Text
                      fontSize='sm'
                      color={style.color}
                      overflow='hidden'
                      textOverflow='ellipsis'
                      whiteSpace='nowrap'>
                      <Text as='span' fontWeight='semibold'>
                        {item.JobIdDisplayName || item.JobIdString}
                      </Text>
                      {item.JobIdDisplayName && item.JobIdDisplayName !== item.JobIdString
                        ? null
                        : ` - ${item.ClientName}`}
                    </Text>
                  </List.Item>
                );
              })}
              {!jobSearchQuery.isFetching && items.length === 0 && searchTerm.length >= 2 && (
                <List.Item px={4} py={2}>
                  <Text fontSize='sm' color='gray.500'>
                    No jobs found
                  </Text>
                </List.Item>
              )}
            </>
          )}
        </List.Root>
      </Portal>
    </Box>
  );
}
